import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useContext,
  useRef,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import cornerstone from 'cornerstone-core';
import cornerstoneTools from 'cornerstone-tools';
import { buildInstanceWadoUrl } from '@ohif/core/src/studies/services/wado/studyInstanceHelpers';
import Axios from 'axios';
import { useAppContext } from '../../context/AppContext';
import useSearchParams from '../../customHooks/useSearchParams';
import {
  getListLessonsType,
  getAttributes,
  getMarkPropertyList,
  getMarkList,
} from './api';
import { setMarkList, setPagination } from '@ohif/viewer/src/store/mark/action';
import { message } from 'antd';

// 获取图层数据
const useImage = ({ studies, activeViewport, activeIndex }) => {
  let firstIndexNumber = 1;
  let desc = true;
  let lastIndexNumber = 1;
  let instanceNumber = firstIndexNumber;
  if (activeViewport) {
    const study = studies.find(
      item => item.StudyInstanceUID === activeViewport.StudyInstanceUID
    );
    if (study) {
      const displaySet = study.displaySets.find(
        ds => ds.displaySetInstanceUID === activeViewport.displaySetInstanceUID
      );
      const firstImage = displaySet.getImage(0);
      firstIndexNumber = firstImage.getTagValue('InstanceNumber', 1);
      desc = firstIndexNumber === displaySet.numImageFrames;
      lastIndexNumber = desc ? 1 : displaySet.numImageFrames;
    }

    const element = cornerstoneTools.store.state.enabledElements[activeIndex];
    if (element) {
      const enabledElement = cornerstone.getEnabledElement(element);
      if (enabledElement.image) {
        const imageId = enabledElement.image.imageId;
        const generalImageModule =
          cornerstone.metaData.get('generalImageModule', imageId) || {};
        instanceNumber = generalImageModule.instanceNumber;
      }
    }
  }

  return { desc, firstIndexNumber, lastIndexNumber, instanceNumber };
};

// 是否为科研版
export const useIsResearch = () => {
  const [{ pro }] = useSearchParams();
  return !isNaN(pro);
};

// 病变类型集
export const useLessonsTypeList = () => {
  const [{ pro }] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [lessonsTypeList, setLessonsTypeList] = useState([]);
  const isEmptyLessonsTypeList = useMemo(
    () => !firstLoad && !lessonsTypeList.length,
    [firstLoad, lessonsTypeList.length]
  );
  useEffect(() => {
    setLoading(true);
    let params = {};
    // 科研版
    const imageDataScienceId = Number(pro);
    if (!Number.isNaN(imageDataScienceId))
      params = {
        isScience: true,
        imageDataScienceId,
      };
    getListLessonsType(params)
      .then(({ data }) =>
        setLessonsTypeList(
          data.map(item => ({
            key: item.name,
            value: item.id.toString(),
            isExam: item.isExam,
          }))
        )
      )
      .finally(() => {
        setLoading(false);
        if (firstLoad) setFirstLoad(false);
      });
  }, [firstLoad, pro]);

  return [lessonsTypeList, loading, isEmptyLessonsTypeList];
};

// 标注属性列表
export const usePropertyList = (lesionsId, isExam) => {
  const isScience = useIsResearch();
  const [loading, setLoading] = useState(false);
  const [propertyList, setPropertyList] = useState([]);
  if (lesionsId == 2) {
    isExam = true;
  }
  useEffect(() => {
    if (!lesionsId) return;
    setLoading(true);
    getAttributes({ lesionsId, isScience, isExam })
      .then(({ list }) => {
        setPropertyList(list || []);
      })
      .finally(() => setLoading(false));
  }, [isExam, isScience, lesionsId, setPropertyList]);
  return [propertyList, loading];
};

// 加载属性列表
export const useGetPropertyList = ({ filterUsers = [] }) => {
  const history = useHistory();
  const [{ aiCheckId: caseId, pro }] = useSearchParams();
  const imgUrl = useMemo(() => {
    let text = history.location.pathname.match(/viewer\/[\d|.]*/) || [];
    text = text.map(v => v.replace(/\w+\//, ''));
    return text[0];
  }, [history.location.pathname]);

  const isScience = useIsResearch();
  const dispatch = useDispatch();

  const [users, setUsers] = useState([]);
  const [apply, setApply] = useState();

  const [loading, setLoading] = useState(false);
  const pagination = useSelector(({ mark }) => mark.pagination);
  const { current, pageSize: size } = pagination;
  const handlePropertyList = useCallback(() => {
    setLoading(true);
    getMarkPropertyList({
      caseId,
      imageDataScienceId: pro,
      img: imgUrl,
      isScience,
      current,
      size,
      accountIds: filterUsers.join(','),
    })
      .then(({ data: res }) => {
        dispatch(setPagination({ total: res.total }));
        const userGroup = {};
        const data = res.records.map(item => {
          const { account } = item;
          if (account) userGroup[account.accountId] = account;
          return {
            properties: JSON.parse(item.data),
            source: item,
          };
        });
        dispatch(setMarkList(data));
        setUsers(Object.values(userGroup));
        //lhwen edit 添加刷新状态参数
        const {
          applyStatus,
          applyStatusCode,
          applyRemark,
          tagListReject,
          refresh,
        } = res.checkVO || {};
        //lhwen 修改 提示用户AI辅助标注是否已完成
        if (refresh === '1') message.info('AI辅助标注已完成！');
        if (applyRemark)
          setApply({
            applyStatus,
            applyStatusCode,
            applyRemark,
            tagListReject,
          });
      })
      .finally(() => setLoading(false));
  }, [caseId, current, dispatch, filterUsers, imgUrl, isScience, pro, size]);

  return {
    handlePropertyList,
    pagination,
    loading,
    users,
    apply,
  };
};

// 获取阅片器元素
const getElement = activeViewportIndex =>
  cornerstoneTools.store.state.enabledElements[activeViewportIndex];

// 获取ImageID
export const useGetImageId = () => {
  const appContext = useAppContext();
  const getImageId = useCallback(
    (img, ser, sopUid) =>
      'dicomweb:' +
      buildInstanceWadoUrl.apply(null, [
        appContext.appConfig.servers.dicomWeb[0],
        img,
        ser,
        sopUid,
      ]),
    [appContext.appConfig.servers.dicomWeb]
  );
  return getImageId;
};

const drawing = time => {
  return new Promise(resolve => {
    const waiting = () =>
      drawContext.state
        ? setTimeout(waiting, time)
        : resolve(drawContext.state);
    waiting();
  });
};
// 绘图状态管理
const drawContext = {
  state: false,
  setDrawing: state => {
    drawContext.state = state;
    if (!state) drawContext.drawing = drawing;
  },
  drawing,
};

export const DrawContext = React.createContext(drawContext);

// 获取绘图数据;
export const useGetDrawList = (
  viewports,
  { filterIds = [], filterUsers = [], pagination = { pageSize: 10 } },
  props
) => {
  const [{ aiCheckId: caseId, pro }] = useSearchParams();
  const { activeViewportIndex, viewportSpecificData } = viewports;
  const viewportSpecific = viewportSpecificData[activeViewportIndex] || {};
  const { SeriesInstanceUID, StudyInstanceUID } = viewportSpecific;
  const getImageId = useGetImageId();

  const { setDrawing } = useContext(DrawContext);
  const [drawList, setDrawList] = useState([]);
  const { desc, firstIndexNumber, lastIndexNumber, instanceNumber } = useImage(
    props
  );
  const imgSize = desc ? -5 : 5;
  const [imgPage, setImgPage] = useState(instanceNumber);
  useEffect(() => setImgPage(instanceNumber), [instanceNumber]);
  const [startImg, endImg] = useMemo(() => {
    let startImg = imgPage - imgSize;
    let endImg = imgPage + imgSize;
    if (desc) {
      if (startImg > firstIndexNumber) startImg = firstIndexNumber;
      if (endImg < lastIndexNumber) endImg = lastIndexNumber;
      [startImg, endImg] = [endImg, startImg];
    } else {
      if (startImg < firstIndexNumber) startImg = firstIndexNumber;
      if (endImg > lastIndexNumber) endImg = lastIndexNumber;
    }
    return [startImg, endImg];
  }, [desc, firstIndexNumber, imgPage, imgSize, lastIndexNumber]);

  // 阅片器滚动触发翻页
  useEffect(() => {
    const element = getElement(activeViewportIndex);
    if (!element) return;
    const genSetImageRange = debounce(() => {
      const enabledElement = cornerstone.getEnabledElement(element);
      const { imageId } = enabledElement.image;
      const generalImageModule =
        cornerstone.metaData.get('generalImageModule', imageId) || {};
      const { instanceNumber } = generalImageModule;
      setImgPage(instanceNumber);
    }, 500);
    element.addEventListener(
      cornerstoneTools.EVENTS.STACK_SCROLL,
      genSetImageRange
    );
    return () =>
      element.removeEventListener(
        cornerstoneTools.EVENTS.STACK_SCROLL,
        genSetImageRange
      );
  }, [activeViewportIndex, imgSize]);

  const cancelRef = useRef(null);
  const handleDrawList = useCallback(() => {
    setDrawing(true);
    const cancelSource = Axios.CancelToken.source();
    if (cancelRef.current) cancelRef.current.cancel();
    cancelRef.current = cancelSource;
    getMarkList(
      {
        imageDataScienceId: pro,
        caseId,
        ser: SeriesInstanceUID,
        img: StudyInstanceUID,
        startImg,
        endImg,
        size: pagination.pageSize,
        accountIds: filterUsers.join(','),
      },
      { cancelToken: cancelSource.token }
    ).then(res => res.list && setDrawList(res.list));
  }, [
    setDrawing,
    startImg,
    endImg,
    pro,
    caseId,
    SeriesInstanceUID,
    StudyInstanceUID,
    pagination.pageSize,
    filterUsers,
  ]);

  const activeMarkUUID = useSelector(({ mark }) => mark.activeMarkUUID);
  useEffect(() => {
    // 获取视图元素
    const element = getElement(activeViewportIndex);
    if (!element) return;
    const {
      toolState,
      addImageIdToolState,
      clearImageIdToolState,
    } = cornerstoneTools.globalImageIdSpecificToolStateManager;
    // 批量清除图层绘图数据
    Object.keys(toolState).forEach(clearImageIdToolState);
    drawList.forEach(item => {
      if (filterIds.includes(item.tagResultDetail)) return;
      const toolType = item.type;
      const measurementData = JSON.parse(item.areaData);
      if (
        measurementData.handles.textBox &&
        measurementData.handles.textBox.boundingBox &&
        measurementData.handles.textBox.boundingBox.left === undefined
      ) {
        measurementData.handles.textBox.boundingBox.left = 0;
        measurementData.handles.textBox.boundingBox.top = 0;
      }
      if (
        measurementData.polyBoundingBox &&
        measurementData.polyBoundingBox.left === undefined
      )
        measurementData.polyBoundingBox = {
          left: 0,
          top: 0,
          width: 0,
          height: 0,
        };
      const imageId = getImageId(
        StudyInstanceUID,
        SeriesInstanceUID,
        item.sopUid
      );
      measurementData.active = measurementData.uuid === activeMarkUUID;
      // 记录当前页用于区分是否为新建绘图数据
      measurementData.currentPage = item.current;
      addImageIdToolState(imageId, toolType, measurementData);
    });
    try {
      cornerstone.updateImage(element);
      setDrawing(false);
    } catch (error) {
      console.error(error);
    }
  }, [
    activeViewportIndex,
    drawList,
    getImageId,
    setDrawing,
    SeriesInstanceUID,
    StudyInstanceUID,
    filterIds,
    activeMarkUUID,
  ]);

  return [handleDrawList];
};

// 在阅片器上高亮标注
export const useChangeActiveMark = () => {
  const drawContext = useContext(DrawContext);
  const drawingRef = useRef();
  drawingRef.current = drawContext.drawing;

  const changeActiveMark = async (imageId, toolType, measurementId) => {
    // 如果在加载绘图数据时,等待完成
    if (drawContext.drawing) await drawContext.drawing(100);
    const toolState = cornerstoneTools.globalImageIdSpecificToolStateManager.getImageIdToolState(
      imageId,
      toolType
    );
    if (!toolState) return;
    toolState.data.forEach(item => {
      item.active = item.uuid === measurementId;
    });
  };
  return changeActiveMark;
};
